import { getConfig } from "../config";

export interface User {
  profile: { sub: string; preferred_username: string; name: string; matrix_id: string; groups: string[] };
}
export interface Session { user: User; csrf_token: string }
let session: Session | null = null;
let loading: Promise<Session | null> | null = null;
let lastInteraction = Date.now();
let lastActivitySent = 0;
let uploads = 0;
let automaticLogin = true;

export function allowAutomaticLogin(): boolean { return automaticLogin; }
export function suppressAutomaticLogin(): void { automaticLogin = false; }
export function resumeAutomaticLogin(): void { automaticLogin = true; }

export class SessionError extends Error {
  constructor(public status: number, public code: string) { super(code); }
}
export function apiURL(path: string): string {
  const url = new URL(getConfig().api_base_url.replace(/\/$/, "") + path, location.origin);
  if (url.origin !== location.origin) throw new Error("API must be same-origin");
  return url.pathname + url.search;
}
export function markInteraction(): void { lastInteraction = Date.now(); }
export function uploadActivity(start: boolean): void { uploads = Math.max(0, uploads + (start ? 1 : -1)); }
export function activityHeaders(): Record<string, string> {
  const now = Date.now();
  const playing = [...document.querySelectorAll<HTMLMediaElement>("video,audio")].some(v => !v.paused && !v.ended);
  const active = uploads > 0 || playing || (!document.hidden && now - lastInteraction < 60_000);
  if (!active || now - lastActivitySent < 30_000) return {};
  lastActivitySent = now;
  return { "X-Recordings-Activity": "1" };
}
export async function processAuthError(res: Response): Promise<void> {
  if (res.status !== 401 && res.status !== 403) return;
  const body = await res.clone().json().catch(() => ({})) as { error?: string };
  if (res.status === 401 || body.error === "access_revoked") {
    session = null;
    window.dispatchEvent(new CustomEvent("recordings:auth-lost", { detail: body.error === "access_revoked" ? "access_revoked" : "login_required" }));
  }
}
export async function loadSession(): Promise<Session | null> {
  if (loading) return loading;
  loading = (async () => {
    const res = await fetch(apiURL("/auth/session"), { credentials: "same-origin", cache: "no-store", headers: activityHeaders() });
    if (res.status === 401) { session = null; return null; }
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      if (body.error === "access_revoked") session = null;
      throw new SessionError(res.status, body.error ?? "auth_unavailable");
    }
    session = await res.json() as Session;
    return session;
  })().finally(() => { loading = null; });
  return loading;
}
export async function csrfHeaders(): Promise<Record<string, string>> {
  const s = session ?? await loadSession();
  if (!s) throw new SessionError(401, "login_required");
  return { "X-CSRF-Token": s.csrf_token };
}
export function startLogin(returnTo = location.pathname + location.search + location.hash): void {
  if (returnTo.startsWith("/login") || returnTo.startsWith("/auth/")) returnTo = "/";
  location.assign(apiURL("/auth/login") + "?return_to=" + encodeURIComponent(returnTo));
}
export async function endSession(): Promise<void> {
  // Stop guards reacting to in-flight 401s while the logout navigation loads.
  suppressAutomaticLogin();
  const headers = await csrfHeaders();
  const res = await fetch(apiURL("/auth/logout"), { method: "POST", credentials: "same-origin", headers });
  if (!res.ok) throw new SessionError(res.status, "logout_failed");
  session = null;
}
export async function sendActivity(): Promise<void> {
  const activity = activityHeaders();
  if (!activity["X-Recordings-Activity"] || !session) return;
  const res = await fetch(apiURL("/auth/activity"), { method: "POST", credentials: "same-origin", headers: { ...activity, ...await csrfHeaders() } });
  await processAuthError(res);
}
